/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CollectionRunHistory.tsx
 *     Feature         :   143-collection-job-decomposition
 *     Description     :   Paginated run history table with status badges and tier filter (FR-008, FR-009)
 *     Constitution    :   v1.12.0 | Principle XXXVII (Code Headers)
 *     Author          :   Peter Dobler
 *     Created         :   2026-03-02
 * =========================================================================================
 */

import React, { useRef, useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Loader2, ChevronRight, ChevronDown } from 'lucide-react';
import { usePagination } from '../../hooks/usePagination';
import { PaginationControls } from '../../components/operations/PaginationControls';
import { useCollectionRuns } from '../../services/operations/collectionJobService';
import { CollectionRunDetail } from './CollectionRunDetail';
import { LoadingSpinner } from '../../components/operations/LoadingSpinner';
import type { CollectionRun } from '../../types/collectionJob';
import { formatEastern } from '@/utils/dateFormatter';

interface CollectionRunHistoryProps {
  selectedRunId: string | null;
  onSelectRun: (runId: string) => void;
}

const TIER_FILTERS = [
  { label: 'All', value: undefined },
  { label: 'daily', value: 'daily' },
  { label: '2x-daily', value: '2x-daily' },
  { label: '4x-daily', value: '4x-daily' },
  { label: 'hourly', value: 'hourly' },
  { label: '30min', value: '30min' },
  { label: '15min', value: '15min' },
  { label: '5min', value: '5min' },
] as const;

type TierValue = (typeof TIER_FILTERS)[number]['value'];

const TIER_COLORS: Record<string, string> = {
  'daily':    'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  '2x-daily': 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  '4x-daily': 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  'hourly':   'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  '30min':    'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  '15min':    'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  '5min':     'bg-lime-50 text-lime-700 dark:bg-lime-900/30 dark:text-lime-300',
};

/** Format seconds to "Xm Ys" */
function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** Format ISO date */
function formatDate(iso: string): string {
  return formatEastern(iso);
}

/** Status badge (FR-008) */
function StatusBadge({ status }: { status: CollectionRun['status'] }) {
  const config = {
    Completed: { icon: CheckCircle2, color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30', label: 'Completed' },
    Partial:   { icon: AlertTriangle, color: 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30', label: 'Partial' },
    Failed:    { icon: XCircle, color: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30', label: 'Failed' },
    Running:   { icon: Loader2, color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30', label: 'Running' },
  }[status] ?? { icon: CheckCircle2, color: 'text-[var(--color-text-secondary)] bg-[var(--color-bg-subtle)]', label: status };

  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className={`w-3 h-3 ${status === 'Running' ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
}

/** Tier badge */
function TierBadge({ tier }: { tier: string | null }) {
  if (!tier) return <span className="text-[var(--color-text-secondary)] text-xs">—</span>;
  const colorClass = TIER_COLORS[tier] ?? 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {tier}
    </span>
  );
}

/** Inline detail wrapper — auto-scrolls into view on mount */
function InlineDetail({ runId }: { runId: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);
  return (
    <div ref={ref} className="px-2 py-3">
      <CollectionRunDetail runId={runId} />
    </div>
  );
}

export const CollectionRunHistory: React.FC<CollectionRunHistoryProps> = ({
  selectedRunId,
  onSelectRun,
}) => {
  const [activeTier, setActiveTier] = useState<TierValue>(undefined);
  const pagination = usePagination({ totalItems: 0, initialPageSize: 20 });
  const offset = (pagination.currentPage - 1) * pagination.pageSize;
  const { data, isLoading, error } = useCollectionRuns(pagination.pageSize, offset, activeTier);

  const totalItems = data?.pagination?.total ?? 0;
  const effectivePagination = usePagination({ totalItems, initialPageSize: pagination.pageSize });

  if (isLoading) return <LoadingSpinner message="Loading run history..." />;
  if (error) return <div className="text-red-500 text-sm">Failed to load run history</div>;

  const runs = data?.runs ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[var(--color-text-secondary)]">
          Run History
        </h3>
        <div className="flex items-center gap-1 flex-wrap">
          {TIER_FILTERS.map(({ label, value }) => (
            <button
              key={label}
              onClick={() => setActiveTier(value)}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                activeTier === value
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] hover:opacity-80'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {runs.length === 0 ? (
        <p className="text-sm text-[var(--color-text-secondary)] py-8 text-center">
          No collection runs found
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--color-text-secondary)] border-b-2 border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
                  <th className="px-2 py-1.5 w-6"></th>
                  <th className="px-2 py-1.5 pr-4 font-medium">Run ID</th>
                  <th className="px-2 py-1.5 pr-4 font-medium">Started</th>
                  <th className="px-2 py-1.5 pr-4 font-medium">Tier</th>
                  <th className="px-2 py-1.5 pr-4 font-medium">Duration</th>
                  <th className="px-2 py-1.5 pr-4 font-medium">Status</th>
                  <th className="px-2 py-1.5 font-medium text-right">Entities</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  const isExpanded = selectedRunId === run.id;
                  return (
                    <React.Fragment key={run.id}>
                      <tr
                        onClick={() => onSelectRun(run.id)}
                        className={`
                          cursor-pointer transition-colors hover:bg-[var(--color-bg-subtle)]
                          ${isExpanded
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-b-0'
                            : 'border-b border-[var(--color-border)]'}
                        `}
                      >
                        <td className="py-2.5 pr-4 pl-1 w-6">
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4 text-blue-500" />
                            : <ChevronRight className="w-4 h-4 text-[var(--color-text-secondary)]" />}
                        </td>
                        <td className="py-2.5 pr-4 font-mono text-xs text-[var(--color-text-secondary)]">
                          {run.id.substring(0, 8)}
                        </td>
                        <td className="py-2.5 pr-4 text-[var(--color-text-secondary)]">
                          {formatDate(run.started_at)}
                        </td>
                        <td className="py-2.5 pr-4">
                          <TierBadge tier={run.tier_name} />
                        </td>
                        <td className="py-2.5 pr-4 text-[var(--color-text-secondary)]">
                          {formatDuration(run.duration_seconds)}
                        </td>
                        <td className="py-2.5 pr-4">
                          <StatusBadge status={run.status} />
                        </td>
                        <td className="py-2.5 text-right text-[var(--color-text-secondary)]">
                          {run.total_entities.toLocaleString()}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-[var(--color-border)]">
                          <td colSpan={7} className="p-0">
                            <InlineDetail runId={run.id} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <PaginationControls
              currentPage={effectivePagination.currentPage}
              totalPages={effectivePagination.totalPages}
              totalItems={totalItems}
              pageSize={effectivePagination.pageSize}
              onPageChange={effectivePagination.goToPage}
              onPageSizeChange={effectivePagination.setPageSize}
            />
          </div>
        </>
      )}
    </div>
  );
};
